import React from 'react';
import { Box } from '@mui/material';
import { motion } from 'framer-motion';
import Text from '../Text';
import SectionHeader from '../SectionHeader';
import ScrollReveal from '../animated/ScrollReveal';
import { colors, gradients } from '../../theme/designTokens';

const oldWorld = ['Insurance', 'Claims', 'Recovery'];
const newWorld = ['Prediction', 'Preparedness', 'Response', 'Recovery', 'Resilience'];

const FlowColumn = ({ title, steps, accent, slideFrom = 'left' }) => (
  <Box sx={{ width: '100%', maxWidth: 360 }}>
    <Text variant="label" sx={{ color: accent, mb: 3, display: 'block' }}>
      {title}
    </Text>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {steps.map((step, i) => (
        <Box
          key={step}
          component={motion.div}
          initial={{ opacity: 0, x: slideFrom === 'left' ? -12 : 12 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: i * 0.08 }}
        >
          <Box
            sx={{
              py: 2,
              px: 2.5,
              borderLeft: `2px solid ${accent}`,
              background: i % 2 === 0 ? 'rgba(15, 23, 42, 0.12)' : 'transparent',
            }}
          >
            <Text variant="h4" color="primary">
              {step}
            </Text>
          </Box>
          {i < steps.length - 1 && (
            <Box sx={{ pl: 2.5, py: 0.5, color: colors.navyMid, fontSize: '0.75rem' }}>↓</Box>
          )}
        </Box>
      ))}
    </Box>
  </Box>
);

const WorldChanged = () => (
  <Box
    sx={{
      px: { xs: 2, sm: 3, md: 4 },
      py: { xs: 8, md: 12 },
      background: gradients.sectionLight,
    }}
  >
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <ScrollReveal>
        <SectionHeader
          eyebrow="The World Has Changed"
          title="Risk has changed. Your protection strategy should too."
          highlightWords={['changed']}
          subtitle="Today's organizations operate where cyber threats, supply chain failures, regulatory shifts, climate events, geopolitical instability, and operational disruptions occur simultaneously."
        />
      </ScrollReveal>

      <ScrollReveal delay={0.1}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr auto 1fr' },
            gap: { xs: 3, md: 4 },
            alignItems: 'start',
            justifyItems: { xs: 'stretch', md: 'center' },
            mb: 6,
          }}
        >
          <FlowColumn title="Old World" steps={oldWorld} accent={colors.navy} slideFrom="left" />

          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              justifyContent: 'center',
              alignSelf: 'stretch',
              px: 1,
              color: colors.navy,
              fontSize: '0.75rem',
              fontWeight: 600,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              borderLeft: `1px solid ${colors.navy}`,
              borderRight: `1px solid ${colors.navy}`,
              minHeight: 200,
            }}
          >
            vs
          </Box>

          <Box
            sx={{
              display: { xs: 'block', md: 'none' },
              width: '100%',
              height: 1,
              background: colors.navyMid,
            }}
          />

          <FlowColumn title="New World" steps={newWorld} accent={colors.navy} slideFrom="right" />
        </Box>
      </ScrollReveal>

      <ScrollReveal delay={0.15}>
        <Box sx={{ maxWidth: 720, mx: 'auto', textAlign: 'center' }}>
          <Text variant="body" color="secondary" sx={{ mb: 2, display: 'block' }}>
            Insurance alone is no longer enough. Organizations need resilience.
          </Text>
          <Text variant="h3" color="primary">
            ALEgION helps businesses build that resilience.
          </Text>
        </Box>
      </ScrollReveal>
    </Box>
  </Box>
);

export default WorldChanged;
